import { formatAmountWithTwoDecimalPlaces } from '../utils/function';
import { formatUsernameDisplay } from '../utils/usernameFormat';
import { showForegroundPushToast } from './fcm/PushForegroundToast';

const resolveUserInfoForToast = (userInfo = {}) => ({
  username: userInfo?.username || userInfo?.user_data?.username || '',
  avatar:
    userInfo?.profile_picture ||
    userInfo?.profilePicture ||
    userInfo?.user_data?.profile_picture ||
    null,
});

const buildToastTag = (prefix) => `${prefix}-${Date.now()}`;

const showSimplePushToast = ({ title, body, type, notificationType, tagPrefix, extraData = {} }) => {
  showForegroundPushToast({
    title,
    body,
    type,
    data: {
      type,
      notificationType,
      message: body,
      ...extraData,
    },
    tag: buildToastTag(tagPrefix),
  });
};

const showAvatarPushToast = ({
  title,
  body,
  type,
  notificationType,
  userInfo = {},
  tagPrefix,
  extraData = {},
}) => {
  const { username, avatar } = resolveUserInfoForToast(userInfo);

  showForegroundPushToast({
    title,
    body,
    type,
    data: {
      type,
      notificationType,
      message: body,
      username,
      creator_username: username,
      creator_avatar: avatar,
      avatar,
      ...extraData,
    },
    tag: buildToastTag(tagPrefix),
  });
};

/** Manual success toast with user/creator avatar (or default user icon). */
export const showManualSuccessPushToast = (title, message, userInfo = {}) => {
  showAvatarPushToast({
    title,
    body: message,
    type: 'profile',
    notificationType: 'profile_updated',
    userInfo,
    tagPrefix: 'settings-success',
  });
};

/**
 * Activity icon type for purchase payment errors (see getActivityIcon).
 * @param {string} [purchaseType] - e.g. PurchaseContent `purchase_type` or `credits`
 */
export const resolvePurchaseFailedActivityType = (purchaseType) => {
  switch (purchaseType) {
    case 'message_content':
      return 'bundles';
    case 'credits':
      return 'purchase_credits';
    case 'content':
    default:
      return 'ppv';
  }
};

/** Content unlocked after successful PPV purchase — uses unlock icon. */
export const showContentUnlockedPushToast = (purchaseType = 'content', creatorInfo = {}) => {
  const { username } = resolveUserInfoForToast(creatorInfo);
  const displayUsername = formatUsernameDisplay(username);
  const message = displayUsername
    ? `Enjoy your new exclusive content from ${displayUsername}`
    : 'Enjoy your new exclusive content';

  showAvatarPushToast({
    title: 'Unlocked 🎉',
    body: message,
    type: 'unlock',
    notificationType: 'content_unlocked',
    userInfo: creatorInfo,
    tagPrefix: `content-unlocked-${purchaseType}`,
    extraData: { purchaseType },
  });
};

/** Purchase failed toast — icon follows purchase module (PPV lock vs message media). */
export const showPurchaseFailedPushToast = (title, message, purchaseType = 'content') => {
  const activityType = resolvePurchaseFailedActivityType(purchaseType);

  showSimplePushToast({
    title,
    body: message,
    type: activityType,
    notificationType: 'purchase_failed',
    tagPrefix: `purchase-failed-${purchaseType}`,
    extraData: { purchaseType },
  });
};

/** Tip failed toast with the same tip icon used in activity. */
export const showTipFailedPushToast = (title, message) => {
  showSimplePushToast({
    title,
    body: message,
    type: 'tip',
    notificationType: 'tip_failed',
    tagPrefix: 'tip-failed',
  });
};

/** Subscription canceled after successful unsubscribe — uses creator avatar. */
export const showSubscriptionCancelledPushToast = (creatorInfo = {}, message) => {
  const { username } = resolveUserInfoForToast(creatorInfo);
  const displayUsername = formatUsernameDisplay(username);
  const title = displayUsername
    ? `Successfully Unsubscribed ${displayUsername}`
    : 'Successfully Unsubscribed';
  const body =
    (message && String(message).trim()) || 'This subscription has been successfully canceled.';

  showAvatarPushToast({
    title,
    body,
    type: 'subscription',
    notificationType: 'subscription_cancelled',
    userInfo: creatorInfo,
    tagPrefix: 'subscription-cancelled',
  });
};

export const showSubscriptionSuccessPushToast = (creatorInfo = {}) => {
  const { username } = resolveUserInfoForToast(creatorInfo);
  const displayUsername = formatUsernameDisplay(username);
  const message = displayUsername
    ? `Your subscription to ${displayUsername} is now active`
    : 'Your subscription is now active';

  showAvatarPushToast({
    title: 'Successfully subscribed 🎊',
    body: message,
    type: 'subscription',
    notificationType: 'subscription_success',
    userInfo: creatorInfo,
    tagPrefix: 'subscription-success',
  });
};

export const showTipSentPushToast = (amount, creatorInfo = {}) => {
  const { username } = resolveUserInfoForToast(creatorInfo);
  const formattedAmount = formatAmountWithTwoDecimalPlaces(amount);
  const displayUsername = formatUsernameDisplay(username);
  const message = displayUsername
    ? `You sent a $${formattedAmount} tip to ${displayUsername}`
    : `You sent a $${formattedAmount} tip`;

  showAvatarPushToast({
    title: 'Tip Sent 🎉',
    body: message,
    type: 'tip',
    notificationType: 'tip_sent',
    userInfo: creatorInfo,
    tagPrefix: 'tip-sent',
    extraData: { amount },
  });
};

export const showPromotionActivePushToast = (creatorInfo = {}) => {
  showAvatarPushToast({
    title: 'Offer Is Live 🎉',
    body: 'Your promotion is now active.',
    type: 'promotion',
    notificationType: 'promotion_active',
    userInfo: creatorInfo,
    tagPrefix: 'promotion-active',
  });
};

export const showProfileUpdatedPushToast = (creatorInfo = {}) => {
  showManualSuccessPushToast('Success', 'Profile updated successfully!', creatorInfo);
};

export const showPromotionCancelledPushToast = (creatorInfo = {}) => {
  showAvatarPushToast({
    title: 'Success',
    body: 'Campaign cancelled successfully!',
    type: 'promotion',
    notificationType: 'promotion_cancelled',
    userInfo: creatorInfo,
    tagPrefix: 'promotion-cancelled',
  });
};

/** Call credits added after successful purchase — uses phone icon. */
export const showCallCreditsAddedPushToast = (creditsAmount) => {
  const credits = Number(creditsAmount);
  const normalizedCredits = Number.isFinite(credits) ? credits : 0;
  const message = `You added ${normalizedCredits} call credits to your account`;

  showSimplePushToast({
    title: 'Call Credits',
    body: message,
    type: 'credits',
    notificationType: 'call_credits_added',
    tagPrefix: 'call-credits-added',
    extraData: { creditsAmount: normalizedCredits },
  });
};
